import React, { useState, useRef, useEffect, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { EyeIcon, CaretDownIcon, MagnifyingGlassIcon, CircleNotchIcon, UserIcon } from "@phosphor-icons/react";

const AdminImpersonation = ({ impersonating }) => {
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/users");
      setUsers(res.data.users || []);
    } catch (e) {
      toast.error("Could not load users");
    } finally {
      setLoading(false);
    }
  }, []);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && users.length === 0) load();
  };

  const become = async (email) => {
    setBusy(true);
    try {
      await api.post("/admin/impersonate", { email });
      toast.success(`Now viewing as ${email}`);
      window.location.reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not switch user");
      setBusy(false);
    }
  };

  const filtered = users.filter((u) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return (u.email || "").toLowerCase().includes(s) || (u.name || "").toLowerCase().includes(s);
  });

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="admin-viewas-button"
        onClick={toggle}
        className={`flex items-center gap-2 border px-2.5 py-1.5 rounded-sm transition-colors duration-200 ${
          impersonating
            ? "border-[#FFB800]/60 bg-[#FFB800]/10 text-[#FFB800]"
            : "border-[#16C784]/40 bg-[#16C784]/5 hover:bg-[#16C784]/15 text-[#16C784]"
        }`}
        title="Admin: view as user"
      >
        <EyeIcon size={15} weight="fill" />
        <span className="font-mono text-[9px] uppercase tracking-wider hidden sm:inline">
          {impersonating ? "Viewing as user" : "View as"}
        </span>
        <CaretDownIcon size={13} className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          data-testid="admin-user-menu"
          className="absolute right-0 top-full mt-2 w-80 bg-[#0C0C0E] border border-[#222226] rounded-sm shadow-2xl overflow-hidden z-50"
          style={{ boxShadow: "0 0 30px rgba(0,0,0,0.6)" }}
        >
          <div className="px-3 py-2 text-[10px] font-bold uppercase tracking-[0.2em] text-[#5a5a63] border-b border-[#1a1a1e] flex items-center justify-between">
            <span>Admin · All Users {users.length ? `· ${users.length}` : ""}</span>
          </div>
          <div className="p-2 border-b border-[#1a1a1e]">
            <div className="flex items-center gap-2 bg-[#050505] border border-[#222226] px-2.5 py-1.5">
              <MagnifyingGlassIcon size={13} className="text-[#5a5a63]" />
              <input
                data-testid="admin-user-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search email or name"
                className="flex-1 bg-transparent text-white font-mono text-xs outline-none placeholder:text-[#3a3a40]"
              />
            </div>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <CircleNotchIcon size={22} className="text-[#16C784] spin-slow" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="py-8 text-center font-mono text-[11px] text-[#5a5a63]">No users found</div>
            ) : (
              filtered.map((u) => {
                const isCurrent = impersonating?.email === u.email;
                return (
                  <button
                    key={u.user_id}
                    data-testid={`admin-user-${u.email}`}
                    disabled={busy}
                    onClick={() => become(u.email)}
                    className={`w-full group flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-white/5 transition-colors duration-150 disabled:opacity-50 ${
                      isCurrent ? "bg-[#FFB800]/[0.06]" : ""
                    }`}
                  >
                    {u.picture ? (
                      <img src={u.picture} alt="" referrerPolicy="no-referrer" className="w-8 h-8 rounded-full border border-[#222226] shrink-0" />
                    ) : (
                      <div className="w-8 h-8 rounded-full border border-[#222226] flex items-center justify-center shrink-0">
                        <UserIcon size={15} className="text-[#5a5a63]" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-rajdhani font-semibold text-sm text-white truncate">{u.name || u.email}</div>
                      <div className="font-mono text-[10px] text-[#5a5a63] truncate">{u.email} · {u.channel_count || 0} ch</div>
                    </div>
                    {isCurrent && (
                      <span className="font-mono text-[9px] uppercase tracking-wider text-[#FFB800] shrink-0">Active</span>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminImpersonation;
